import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { readLangCookie, detectSupportedLang } from "@/lib/translate";

// Display-only currency estimates. Checkout is always charged in USD.
export const CURRENCIES = [
  { code: "USD", label: "USD ($)", symbol: "$" },
  { code: "MXN", label: "Mexican Peso (MX$)", symbol: "MX$" },
  { code: "IDR", label: "Indonesian Rupiah (Rp)", symbol: "Rp" },
  { code: "EUR", label: "Euro (€)", symbol: "€" },
  { code: "HKD", label: "Hong Kong Dollar (HK$)", symbol: "HK$" },
  { code: "JMD", label: "Jamaican Dollar (J$)", symbol: "J$" },
  { code: "GBP", label: "British Pound (£)", symbol: "£" },
  { code: "CAD", label: "Canadian Dollar (C$)", symbol: "C$" },
];

const REGION_TO_CCY = { MX: "MXN", ID: "IDR", ES: "EUR", HK: "HKD", JM: "JMD", GB: "GBP", CA: "CAD" };
// Language → currency, so estimates line up with the chosen/translated language.
const LANG_TO_CCY = { es: "MXN", id: "IDR", "zh-TW": "HKD" };

function detectCurrency() {
  try {
    const saved = localStorage.getItem("hf_currency");
    if (saved) return saved; // an explicit user choice always wins
    // If the site is actively translated (or a ?_hl deep-link), match that language.
    const activeLang = readLangCookie();
    if (activeLang && LANG_TO_CCY[activeLang]) return LANG_TO_CCY[activeLang];
    // Otherwise use the browser's region for precision (e.g. es-ES → EUR).
    const loc = (navigator.language || "en-US").split("-");
    const region = (loc[1] || "").toUpperCase();
    if (REGION_TO_CCY[region]) return REGION_TO_CCY[region];
    // Finally, fall back to the browser language (incl. ?_hl) → currency.
    const lang = detectSupportedLang();
    if (lang && LANG_TO_CCY[lang]) return LANG_TO_CCY[lang];
    return "USD";
  } catch {
    return "USD";
  }
}

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState("USD");
  const [rates, setRates] = useState({});

  useEffect(() => { setCurrencyState(detectCurrency()); }, []);

  useEffect(() => {
    let alive = true;
    fetch("https://open.er-api.com/v6/latest/USD")
      .then((r) => r.json())
      .then((d) => { if (alive && d && d.rates) setRates(d.rates); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const setCurrency = useCallback((code) => {
    setCurrencyState(code);
    try { localStorage.setItem("hf_currency", code); } catch { /* ignore */ }
  }, []);

  const meta = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];

  // Returns a localized estimate string, or "" if USD/unavailable.
  const localEstimate = useCallback((usd) => {
    if (currency === "USD" || !rates[currency]) return "";
    const val = Number(usd) * rates[currency];
    const whole = ["IDR", "JMD"].includes(currency);
    const formatted = whole
      ? Math.round(val).toLocaleString()
      : val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${meta.symbol}${formatted}`;
  }, [currency, rates, meta.symbol]);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, rates, localEstimate, symbol: meta.symbol }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext) || { currency: "USD", localEstimate: () => "", setCurrency: () => {} };
